import { useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { resolveAmbassadorReferral } from "@/lib/ambassadorApi";

const REF_KEY = "eva_ref";
const REF_EXP_KEY = "eva_ref_exp";
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

export default function ReferralLand() {
  const { code } = useParams<{ code: string }>();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Inject noindex,nofollow so /r/* URLs never enter search indexes.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!code) {
      setRedirectTo("/");
      return;
    }
    const upper = code.toUpperCase();
    // Optimistic save so attribution survives even if the edge call fails.
    try {
      localStorage.setItem(REF_KEY, upper);
      localStorage.setItem(REF_EXP_KEY, String(Date.now() + NINETY_DAYS_MS));
    } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const result = await resolveAmbassadorReferral(upper, nextParam);
        if (cancelled) return;
        if (result.code) {
          try {
            localStorage.setItem(REF_KEY, result.code.toUpperCase());
            if (result.ttl_ms && Number.isFinite(result.ttl_ms)) {
              localStorage.setItem(REF_EXP_KEY, String(Date.now() + result.ttl_ms));
            }
          } catch { /* ignore */ }
        }
        const dest = (result.redirect_to && typeof result.redirect_to === "string" && result.redirect_to.startsWith("/"))
          ? result.redirect_to
          : (nextParam && nextParam.startsWith("/") ? nextParam : "/");
        setRedirectTo(dest);
      } catch {
        // Function failed — fall back to safe local navigation.
        if (!cancelled) {
          setRedirectTo(nextParam && nextParam.startsWith("/") ? nextParam : "/");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [code, nextParam]);

  if (!redirectTo) return null;
  return <Navigate to={redirectTo} replace />;
}
